import { TranslationKey } from '@Localization';
import { toJS } from 'mobx';
import { InputStatus } from '../../models/error.model';
import { IQuestionModelDto } from './annotations.interface';

export const ImageScopeName = 'Image';

export enum QuestionType {
  TRUE_FALSE = 'TrueFalse',
  ALTERNATIVE = 'Alternative',
  MULTISELECT = 'MultiSelect',
  OPEN = 'Open',
  NUMBER = 'Number',
  STATUS_GOOD_BAD = 'StatusGoodBad',
  SET_TRAIN_TEST = 'SetTrainTest',
}

export interface IAnswerModel {
  id: string;
  text: string;
  selected: boolean;
}

export interface IAnswerProps {
  name: string;
  answer: IAnswerModel;
  disabled: boolean;
  onAnswered: (answerId: string) => void;
}

export interface ITextAnswerProps {
  answer: string;
  disabled: boolean;
  className: string;
  onAnswered(answer: string): void;
}

export interface IQuestionModel extends IQuestionDto {
  status: InputStatus;
}

export interface IQuestionDto {
  projectId: string;
  id: string;
  type: QuestionType;
  isRequired: boolean;
  text: string;
  answer?: string;
  answers: IAnswerModel[];
  scopes: string[];
}

export function isTextQuestion(questionType: QuestionType): boolean {
  return questionType === QuestionType.OPEN || questionType === QuestionType.NUMBER;
}

export function isAttributeQuestion(question: IQuestionModel): boolean {
  return question.scopes[0] !== 'Image';
}

export const answerQuestion = (question: IQuestionModel, answer: string): void => {
  question.status = InputStatus.empty();

  switch (question.type) {
    case QuestionType.TRUE_FALSE:
    case QuestionType.ALTERNATIVE:
    case QuestionType.STATUS_GOOD_BAD:
    case QuestionType.SET_TRAIN_TEST: {
      const selectedAlternativeAnswer = question.answers.find((a) => a.id === answer);
      question.answers
        .filter((x) => x.id !== selectedAlternativeAnswer?.id)
        .forEach((x) => {
          x.selected = false;
        });

      if (selectedAlternativeAnswer) {
        selectedAlternativeAnswer.selected = !selectedAlternativeAnswer.selected;
      }
      break;
    }
    case QuestionType.MULTISELECT: {
      const selectedAnswer = question.answers.find((a) => a.id === answer);
      if (selectedAnswer) {
        selectedAnswer.selected = !selectedAnswer.selected;
      }
      break;
    }
    case QuestionType.OPEN:
    case QuestionType.NUMBER:
      question.answer = answer;
      break;
    default:
      throw new Error(`Question type not found: ${question.type}`);
  }
};

export const validateQuestion = (question: IQuestionModel): boolean => {
  const issues: TranslationKey[] = [];
  if (!isAnswered(question)) {
    issues.push('required_answer_error');
  } else if (question.type === QuestionType.NUMBER) {
    const numberAnswer = Number(question.answer ?? 0);
    if (Number.isNaN(numberAnswer)) {
      issues.push('answer_is_not_valid_number');
    }
  }

  question.status = InputStatus.buildFrom(issues);

  return question.status.isValid ?? true;
};

export function cleanClone(questionModel: IQuestionModel, existingQuestion?: IQuestionModelDto): IQuestionModel {
  return toJS({
    ...questionModel,
    status: InputStatus.empty(),
    answers: questionModel.answers.map((x: IAnswerModel) => {
      const existingAnswer = existingQuestion?.answers.find((y) => y.id === x.id);
      return { id: x.id, selected: existingAnswer?.selected ?? false, text: x.text };
    }),
    answer: existingQuestion?.answer,
  });
}

export function cleanClones(questionModels: IQuestionModel[]): IQuestionModel[] {
  return questionModels.map((qm) => cleanClone(qm));
}

const isAnswered = (question: IQuestionModel): boolean => {
  return (
    !question.isRequired ||
    question.answers.some((a) => a.selected) ||
    (isTextQuestion(question.type) && question.answer !== undefined && question.answer !== null && question.answer.trim() !== '')
  );
};
